import React, { useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import * as actions from '../../../store/index';
import {
  IconButton,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Paper,
  ClickAwayListener,
  MenuList,
  Grow,
  Popper,
  Badge,
  Button,
  FormControl,
  Select,
  Tabs,
  Tab,
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import CloseIcon from '@material-ui/icons/Close';
import SearchBar from './SearchBar/SearchBar';
import ExaminationsListTable from './Tables/ExaminationsListTable';
import expandDrawerIcon from '@iconify/icons-fluent/panel-left-expand-16-regular';
import collapseDrawerIcon from '@iconify/icons-fluent/panel-right-expand-16-regular';
import { fetchPut } from '../../../helpers/fetching';
import bulkActionsIcon from '@iconify/icons-mdi/plus-box-multiple-outline';
import closeBulkIcon from '@iconify/icons-mdi/close-box-multiple';
import WorklistTable from './Tables/WorklistTable/WorklistTable';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import CircularProgress from '@material-ui/core/CircularProgress';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { getUtcDateKeyWithOffset, toIsoDateKey } from '../../../helpers/formatDate';
import { formatPatientName } from '../../../helpers/misc';
import ExamListDateFilterControls from './DatePicker/ExamListDateFilterControls';

const TAB_WORKLIST = 0;
const TAB_EVALUATED = 1;

const WORKLIST_FILTER_OPTIONS = [
  { value: 'all', labelKey: 'all_worklist' },
  { value: '0', labelKey: 'today' },
  { value: '1', labelKey: 'tomorrow' },
  { value: '2', labelKey: 'after_tomorrow' },
];

const nativeLikeSelectMenuProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
  PaperProps: {
    style: {
      marginTop: 2,
      boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
    },
  },
  MenuListProps: {
    dense: true,
    style: { padding: 0 },
  },
};

const useStyles = makeStyles((theme) => ({
  fixedHeader: {
    backgroundColor: 'inherit',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '4vh',
    margin: '3px 0 4px 0',
  },

  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    height: '100%',
    width: '100%',
    overflowX: 'hidden',
  },

  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },

  filtersGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '80%',
  },

  closeButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '4px',
    paddingRight: '10px',
  },

  iconButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  tooltipText: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  menuItemSelected: {
    '&.Mui-selected': {
      background: '#ffcdd229',
      fontWeight: 'bold',
    },
    '&.Mui-selected:hover': {
      background: '#ffcaca14',
    },
  },

  icon_size: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },

  toolbarIconButton: {
    padding: 4,
  },

  toolbarIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
  },

  actionButton: {
    color: 'white',
    minWidth: '80px',
    height: '18px',
    minHeight: '18px',
    margin: '4px',
    fontSize: theme.fonts.responsiveSmall,
    backgroundColor: theme.palette.accents.light,
    '&:hover': {
      background: `${theme.palette.accents.main} !important`,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  bulkActionsSelectedContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  menuItemList: {
    fontSize: theme.fonts.responsiveMedium,
  },

  tabs: {
    maxWidth: '100%',
    height: 'fit-content',
    borderBottom: '1px solid #7f7f7f',
    minHeight: 40,
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.accents.main,
    },
  },

  tab: {
    minHeight: 40,
    width: '50%',
    padding: '4px 12px',
    fontSize: theme.fonts.responsiveMediumBig,
    fontWeight: 'bold',
    letterSpacing: '1px',
    textTransform: 'none',
    background: '#525252',
    borderRadius: '5px 5px 0 0',
    '&.Mui-selected': {
      background: '#5a5a5a',
      color: theme.palette.accents.main,
    },
  },

  selectFilterControl: {
    width: '300px',
    '& .MuiInputBase-root': {
      fontSize: '12px',
      height: 22,
    },
    '& .MuiOutlinedInput-root': {
      height: 26,
      minHeight: 20,
      borderRadius: 4,
    },
    '& .MuiOutlinedInput-input': {
      padding: '1px 4px',
      height: 19,
      minHeight: 18,
    },
    '& .MuiSelect-nativeInput': {
      fontSize: '12px',
    },
  },

  worklistFilterSelect: {
    fontSize: '12px',
    padding: 0,
    textTransform: 'uppercase',
    '& .MuiSelect-select': {
      textTransform: 'uppercase',
      display: 'flex',
      alignItems: 'center',
      fontSize: '12px',
    },
  },

  selectValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  selectMenuItem: {
    fontSize: 12,
    minHeight: 28,
    textTransform: 'uppercase',
  },

  worklistToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '8px 6px',
    flexShrink: 0,
    backgroundColor: '#5a5a5a',
    height: '40px',
  },

  filterDatesGroup: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '8px 6px',
    backgroundColor: '#5a5a5a',
    flexDirection: 'column',
    gap: '4px',
  },
}));

export default function DrawerExamList(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tableEl = useRef(null);
  const worklistTableEl = useRef(null);
  const anchorRef = useRef(null);

  const { order, orderBy } = useSelector((state) => state.examinations);
  const { landscape } = useSelector((state) => state.gui);
  const totalExaminations = useSelector((state) => state.examinations.totalExaminations);
  const bulkAction = useSelector((state) => state.examinations.bulkAction);
  const examList = useSelector((state) => state.examinations.exam_list);
  const global_settings = useSelector((state) => state.global_settings);
  const bulkExaminationsSelected = useSelector((state) => state.examinations.bulkExaminationsSelected);
  const worklist = useSelector((state) => state.examinations.worklist);
  const fetchingWorklist = useSelector((state) => state.examinations.fetchingWorklist);
  const searchedFilter = useSelector((state) => state.filter.search);
  const dates = useSelector((state) => state.dates);

  const [showSortingMenu, setShowSortingMenu] = useState(false);
  const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState(null);
  const [worklistFilter, setWorklistFilter] = useState('all');
  const [listTab, setListTab] = useState(TAB_EVALUATED);

  const worklistActive = global_settings?.premium_features?.query_worklist;
  const hasBulkSelection = bulkExaminationsSelected.length > 0;
  const showWorklistTab = worklistActive && listTab === TAB_WORKLIST;

  const sortMapping = {
    date_desc: { orderBy: 'date', order: 'DESC' },
    date_asc: { orderBy: 'date', order: 'ASC' },
    name_desc: { orderBy: 'name', order: 'DESC' },
    name_asc: { orderBy: 'name', order: 'ASC' },
    date_uploaded_des: { orderBy: 'date_uploaded', order: 'DESC' },
    date_uploaded_asc: { orderBy: 'date_uploaded', order: 'ASC' },
    patient_id_desc: { orderBy: 'patient_id', order: 'DESC' },
    patient_id_asc: { orderBy: 'patient_id', order: 'ASC' },
  };

  const filteredWorklist = useMemo(() => {
    const search = (searchedFilter || '').trim().toLowerCase();

    return worklist.filter((patient) => {
      if (worklistFilter !== 'all') {
        const targetDate = getUtcDateKeyWithOffset(Number(worklistFilter));
        if (toIsoDateKey(patient.scheduled_date) !== targetDate) return false;
      }

      if (!search) return true;

      const name = (patient.patient_name || '').toLowerCase();
      const formattedName = formatPatientName(patient.patient_name || '').toLowerCase();
      const patientId = String(patient.patient_id || '').toLowerCase();

      return name.includes(search) || formattedName.includes(search) || patientId.includes(search);
    });
  }, [worklist, worklistFilter, searchedFilter]);

  const handleWorklistFilterChange = (event) => {
    setWorklistFilter(event.target.value);
  };

  const getDropdownValue = () =>
    Object.entries(sortMapping).find(([, value]) => value.orderBy === orderBy && value.order === order)?.[0];

  const handleSortChange = (value) => {
    const mapping = sortMapping[value];
    if (!mapping) return;
    batch(() => {
      dispatch(actions.setSort(mapping));
      dispatch(actions.setPagination([0, 40]));
    });
    setShowSortingMenu(false);
  };

  const SortDropdown = () => (
    <Popper
      open={showSortingMenu}
      anchorEl={anchorRef.current}
      role={undefined}
      transition
      disablePortal
      style={{ zIndex: 300 }}>
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
          }}>
          <Paper>
            <ClickAwayListener onClickAway={() => setShowSortingMenu(false)}>
              <MenuList>
                {Object.keys(sortMapping).map((key) => (
                  <MenuItem
                    key={key}
                    className={classes.menuItemSelected}
                    selected={key === getDropdownValue()}
                    onClick={() => handleSortChange(key)}
                    id={`sort-${key}`}>
                    {t(`Table.${key}`)}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  const handleBulkActionsMenuOpen = (event) => {
    setBulkActionsAnchorEl(event.currentTarget);
  };

  const handleBulkActionsMenuClose = () => {
    setBulkActionsAnchorEl(null);
  };

  const handleBulkReprocess = () => {
    if (!hasBulkSelection) return;

    const reprocessAction = async () => {
      const response = await fetchPut('reprocess', { exam_ids: bulkExaminationsSelected }, 'bbox');

      dispatch(
        actions.setSnackbar({
          msg: response.success ? 'Table.reprocessing_active' : `api_messages.${response.msg}`,
          severity: response.success ? 'success' : 'error',
        }),
      );
      dispatch(actions.setBulkExaminationsSelected([]));
    };

    if (bulkExaminationsSelected.length > 2) {
      dispatch(
        actions.setActionToConfirm({
          action: reprocessAction,
          text: t('Settings.confirm_reprocess', {
            aproxTime: `${Math.max(1, Math.ceil((bulkExaminationsSelected.length * 30) / 60))}`,
          }),
        }),
      );
    } else {
      reprocessAction();
    }

    handleBulkActionsMenuClose();
  };

  const handleBulkDelete = () => {
    if (!hasBulkSelection) return;

    dispatch(
      actions.setActionToConfirm({
        action: async () => {
          await dispatch(actions.deleteExams(bulkExaminationsSelected, t));
          dispatch(actions.setBulkExaminationsSelected([]));
        },
        text: t('Confirm.delete_exam_confirm', {
          study_name: `${bulkExaminationsSelected.length} ${t('Dashboard.examinations').toLowerCase()}`,
        }),
      }),
    );
    handleBulkActionsMenuClose();
  };

  const onFindPriors = (patientIDs) => {
    dispatch(
      actions.setSnackbar({
        msg: 'Settings.searching_priors',
        severity: 'info',
      }),
    );

    dispatch(
      actions.fetchPriorExaminations(patientIDs, t, () =>
        dispatch(
          actions.setSnackbar({
            msg: 'Settings.priors_found',
            severity: 'success',
          }),
        ),
      ),
    );
    dispatch(actions.setBulkExaminationsSelected([]));
  };

  const handleBulkFetchPriors = () => {
    if (!hasBulkSelection) return;

    const totalTime = bulkExaminationsSelected.length * 30;
    const selectedExamIds = new Set(bulkExaminationsSelected);
    const patientIds = [
      ...new Set(
        examList
          .filter((exam) => selectedExamIds.has(exam.id))
          .map((exam) => exam.patient_id)
          .filter(Boolean),
      ),
    ];
    const searchAction = patientIds.length > 0 ? () => onFindPriors(patientIds) : () => {};

    if (bulkExaminationsSelected.length > 2) {
      dispatch(
        actions.setActionToConfirm({
          action: searchAction,
          buttonOk: patientIds.length > 0 ? t('Confirm.confirm') : t('Settings.dialog_ok'),
          text:
            patientIds.length > 0
              ? t('Settings.confirm_search', {
                  totalExams: bulkExaminationsSelected.length,
                  time_aprox: `${Math.max(1, Math.ceil(totalTime / 60))}`,
                })
              : t('Settings.confirm_search_null'),
        }),
      );
    } else if (patientIds.length > 0) {
      onFindPriors(patientIds);
    }

    handleBulkActionsMenuClose();
  };

  const fetchWorklistManually = (e) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(
      actions.setActionToConfirm({
        action: () => {
          dispatch(actions.fetchWorklist(t, true));
          dispatch(
            actions.setSnackbar({
              msg: 'Settings.fetching_worklist',
              severity: 'info',
            }),
          );
        },
        text: t('Settings.fetch_worklist_manually'),
      }),
    );
  };

  return (
    <div className={classes.drawerContent} style={{ height: '100vh' }}>
      <div className={classes.fixedHeader}>
        <div className={classes.filtersGroup}>
          <SearchBar placeholder={t('Layout.search')} />
        </div>
        <div className={classes.closeButtonContainer} style={{ height: landscape ? '4vh' : '3vh' }}>
          <Menu
            id='bulk-actions-menu'
            anchorEl={bulkActionsAnchorEl}
            keepMounted
            open={Boolean(bulkActionsAnchorEl)}
            onClose={handleBulkActionsMenuClose}>
            <MenuItem onClick={handleBulkReprocess} className={classes.menuItemList}>
              {t('Table.reprocess_bulk', { total: bulkExaminationsSelected.length })}
            </MenuItem>
            <MenuItem onClick={handleBulkDelete} className={classes.menuItemList}>
              {t('Table.delete_bulk', { total: bulkExaminationsSelected.length })}
            </MenuItem>
            <MenuItem
              onClick={handleBulkFetchPriors}
              disabled={!global_settings?.premium_features?.priors_fetching}
              className={classes.menuItemList}>
              {t('Table.priors_bulk_actions', { total: bulkExaminationsSelected.length })}
            </MenuItem>
          </Menu>

          {(totalExaminations > 0 || worklist.length > 0) && (
            <div className={classes.iconButtonContainer}>
              {props.expandedPatientList ? (
                <Tooltip
                  title={
                    <Typography color='inherit' className={classes.tooltipText}>
                      {t('Menu.collapse')}
                    </Typography>
                  }
                  arrow
                  placement='left'>
                  <IconButton onClick={props.handleShrinkToPersistent} size='small' id='expand-patient-list'>
                    <Icon icon={collapseDrawerIcon} className={classes.icon_size} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    <Typography color='inherit' className={classes.tooltipText}>
                      {t('Menu.expand')}
                    </Typography>
                  }
                  arrow
                  placement='left'>
                  <IconButton onClick={props.handleExpandToTemporary} size='small' id='collapse-patient-list'>
                    <Icon icon={expandDrawerIcon} className={classes.icon_size} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}
          <div className={classes.iconButtonContainer}>
            <Tooltip
              arrow
              placement='right'
              title={
                <Typography color='inherit' className={classes.tooltipText}>
                  {t('Console.close')}
                </Typography>
              }>
              <IconButton
                onClick={() => props.handleCloseDrawer(tableEl.current?.scrollTop || 0)}
                size='small'
                id='close-examination-list-drawer'>
                <CloseIcon style={{ transform: 'rotate(180deg)' }} className={classes.icon_size} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>

      {worklistActive && (
        <Tabs value={listTab} onChange={(_event, newValue) => setListTab(newValue)} className={classes.tabs}>
          <Tooltip
            arrow
            placement='bottom-start'
            interactive
            title={
              <Typography color='inherit' className={classes.tooltipText}>
                {t('Table.scheduled_patients_tooltip')}
              </Typography>
            }>
            <Tab
              label={`${t('Table.scheduled_patients')} (${worklist.length})`}
              className={classes.tab}
              id='drawer-tab-worklist'
            />
          </Tooltip>

          <Tooltip
            arrow
            placement='bottom-start'
            interactive
            title={
              <Typography color='inherit' className={classes.tooltipText}>
                {t('Table.evaluated_examinations_tooltip')}
              </Typography>
            }>
            <Tab
              label={`${t('Table.evaluated_examinations')} (${totalExaminations})`}
              className={classes.tab}
              id='drawer-tab-evaluated'
            />
          </Tooltip>
        </Tabs>
      )}

      {showWorklistTab ? (
        <>
          <div className={classes.worklistToolbar}>
            <FormControl
              variant='outlined'
              size='small'
              className={classes.selectFilterControl}
              disabled={worklist.length === 0}>
              <Select
                className={classes.worklistFilterSelect}
                value={worklistFilter}
                onChange={handleWorklistFilterChange}
                MenuProps={nativeLikeSelectMenuProps}
                renderValue={(selected) => {
                  const option =
                    WORKLIST_FILTER_OPTIONS.find((item) => item.value === selected) ||
                    WORKLIST_FILTER_OPTIONS[0];
                  return (
                    <div className={classes.selectValue}>
                      <CalendarTodayIcon style={{ fontSize: 14 }} />
                      {t(`DatePicker.${option.labelKey}`)}
                    </div>
                  );
                }}>
                {WORKLIST_FILTER_OPTIONS.map(({ value, labelKey }) => (
                  <MenuItem key={value} className={classes.selectMenuItem} value={value}>
                    {t(`DatePicker.${labelKey}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip
              arrow
              placement='top-start'
              title={
                <Typography color='inherit' className={classes.tooltipText}>
                  {t('Settings.fetch_worklist')}
                </Typography>
              }>
              {fetchingWorklist ? (
                <CircularProgress size={12} color='secondary' style={{ marginRight: 8 }} />
              ) : (
                <IconButton size='small' className={classes.icon_size} onClick={fetchWorklistManually}>
                  <AutorenewIcon className={classes.icon_size} />
                </IconButton>
              )}
            </Tooltip>
          </div>
          <div className={classes.scrollableContent}>
            <WorklistTable
              tableEl={worklistTableEl}
              expandedPatientList={props.expandedPatientList}
              drawerWidth={props.drawerWidth}
              worklist={filteredWorklist}
            />
          </div>
        </>
      ) : (
        <>
          <div
            className={classes.filterDatesGroup}
            style={{ height: dates.selectedRange === 'custom' || bulkAction ? 'auto' : '42px' }}>
            <ExamListDateFilterControls
              filterIsActive={props.filterIsActive}
              extraActions={
                <>
                  <Tooltip
                    arrow
                    placement='bottom'
                    title={
                      <Typography color='inherit' className={classes.tooltipText}>
                        {t('Table.sort_by')}
                      </Typography>
                    }>
                    <Badge
                      color='secondary'
                      overlap='circular'
                      variant='dot'
                      invisible={orderBy === 'date' && order === 'DESC'}>
                      <IconButton
                        ref={anchorRef}
                        size='small'
                        className={classes.toolbarIconButton}
                        onClick={() => setShowSortingMenu((prevOpen) => !prevOpen)}
                        id='order-examination-list'>
                        <SwapVertIcon className={classes.toolbarIcon} />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                  <SortDropdown />

                  <Tooltip
                    arrow
                    placement='bottom'
                    title={
                      <Typography color='inherit' className={classes.tooltipText}>
                        {bulkAction ? t('Table.disable_bulk_actions') : t('Table.enable_bulk_actions')}
                      </Typography>
                    }>
                    <IconButton
                      color={bulkAction ? 'secondary' : 'default'}
                      size='small'
                      className={classes.toolbarIconButton}
                      onClick={() => dispatch(actions.setBulkAction(!bulkAction))}
                      id='open-examination-list-drawer'>
                      <Icon
                        icon={bulkAction ? closeBulkIcon : bulkActionsIcon}
                        className={classes.toolbarIcon}
                      />
                    </IconButton>
                  </Tooltip>
                </>
              }
            />

            {bulkAction && (
              <div className={classes.bulkActionsSelectedContainer}>
                <Typography
                  color='inherit'
                  className={classes.tooltipText}
                  style={{ fontSize: '10px', marginLeft: '6px' }}>
                  {t('Table.examinations_selected', { total: bulkExaminationsSelected.length })}
                </Typography>
                <Tooltip
                  title={
                    <Typography color='inherit' className={classes.tooltipText}>
                      {t('Table.bulkActions')}
                    </Typography>
                  }>
                  <span>
                    <Button
                      className={classes.actionButton}
                      size='small'
                      disabled={bulkExaminationsSelected.length === 0}
                      onClick={handleBulkActionsMenuOpen}
                      id='bulk-actions-menu-button'>
                      {t('Table.actions')}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>

          <div className={classes.scrollableContent}>
            <ExaminationsListTable
              tableEl={tableEl}
              expandedPatientList={props.expandedPatientList}
              drawerWidth={props.drawerWidth}
            />
          </div>
        </>
      )}
    </div>
  );
}
